import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  PaperPlaneTilt,
  SealCheck,
  Phone,
  VideoCamera,
  DotsThree,
  Smiley,
  Paperclip,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWs, useWsEvent } from "@/context/WebSocketContext";

function hhmm(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

export default function Messages() {
  const { user } = useAuth();
  const { connected, setUnreadMessages } = useWs();
  const [params, setParams] = useSearchParams();
  const deepLinkWith = params.get("with");

  const [conversations, setConversations] = useState([]);
  const [activeHandle, setActiveHandle] = useState(deepLinkWith || null);
  const [messages, setMessages] = useState([]);
  const [activePeer, setActivePeer] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConv, setLoadingConv] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const scrollerRef = useRef(null);

  // Load conversation list
  const refreshList = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data.conversations || []);
      // pick first if no deep link and none active
      if (!activeHandle && !deepLinkWith && (data.conversations || []).length > 0) {
        setActiveHandle(data.conversations[0].peer.handle);
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoadingList(false);
    }
  }, [activeHandle, deepLinkWith]);

  useEffect(() => {
    if (!user || typeof user !== "object") {
      setLoadingList(false);
      return;
    }
    refreshList();
  }, [user, refreshList]);

  // Load active conversation
  useEffect(() => {
    if (!activeHandle || !user || typeof user !== "object") return;
    let cancelled = false;
    setLoadingConv(true);
    setError("");
    api
      .get(`/conversations/${activeHandle}`)
      .then(({ data }) => {
        if (cancelled) return;
        setMessages(data.messages || []);
        setActivePeer(data.conversation?.peer || null);
        // clear unread for the active conv locally
        setConversations((prev) =>
          prev.map((c) =>
            c.peer.handle === activeHandle ? { ...c, unread: 0 } : c
          )
        );
        // also recompute total unread in ws context from freshly reset list
        setUnreadMessages((n) =>
          Math.max(
            0,
            n -
              (conversations.find((c) => c.peer.handle === activeHandle)?.unread || 0)
          )
        );
      })
      .catch((e) => !cancelled && setError(formatApiError(e)))
      .finally(() => !cancelled && setLoadingConv(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHandle, user]);

  // Auto-scroll on new messages
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, activeHandle]);

  // WS: append new incoming messages in realtime
  const onWsMessage = useCallback(
    (data) => {
      if (!user || !data) return;
      const fromMe = data.sender_id === user.id;
      const otherId = fromMe ? data.recipient_id : data.sender_id;
      const otherHandle = fromMe ? null : data.sender?.handle;

      // If message belongs to the currently-open conversation → merge + suppress badge
      if (
        activePeer &&
        (otherHandle === activePeer.handle ||
          otherId === activePeer.id)
      ) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.id)) return prev;
          // Reconcile optimistic (id starts with tmp_) with server echo for the sender
          if (fromMe) {
            const idx = prev.findIndex(
              (m) =>
                typeof m.id === "string" &&
                m.id.startsWith("tmp_") &&
                m.text === data.text
            );
            if (idx !== -1) {
              const next = prev.slice();
              next[idx] = data;
              return next;
            }
          }
          return [...prev, data];
        });
      }

      // Refresh the sidebar list (last_message, unread bumping)
      refreshList();
    },
    [user, activePeer, refreshList]
  );
  useWsEvent("message", onWsMessage);

  const onSend = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !activeHandle) return;
    setDraft("");
    try {
      // Optimistic append
      const optimistic = {
        id: `tmp_${Date.now()}`,
        sender_id: user.id,
        recipient_id: activePeer?.id,
        text,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
      await api.post(`/conversations/${activeHandle}/messages`, { text });
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!user || typeof user !== "object") {
    return (
      <Layout>
        <div className="p-10" data-testid="messages-guest">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
            // MENSAJES PRIVADOS
          </div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-2">
            Ingresa para abrir tu bandeja.
          </h1>
          <Link
            to="/ingresar"
            className="inline-block mt-6 btn-brutal bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-5 py-2"
          >
            → Ingresar
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="messages-page" className="h-screen flex flex-col">
        <header className="border-b border-[#27272A] p-4 md:p-6">
          <div data-testid="ws-status" className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-1">
            INBOX · {conversations.length} CHATS · {connected ? "LIVE ●" : "OFFLINE"}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter uppercase">
            Mensajes
          </h1>
        </header>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-0">
          <aside className="border-b md:border-b-0 md:border-r border-[#27272A] overflow-auto">
            {loadingList ? (
              <div className="p-6 font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                cargando…
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-6">
                <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mb-2">
                  // BANDEJA VACÍA
                </div>
                <p className="font-display text-lg text-[#E5E5E5] leading-snug">
                  Abre un perfil y pulsa{" "}
                  <span className="text-[#FF3E00]">Mensaje</span> para empezar una
                  conversación.
                </p>
              </div>
            ) : (
              conversations.map((c) => {
                const isActive = c.peer.handle === activeHandle;
                return (
                  <button
                    key={c.id}
                    data-testid={`chat-${c.peer.handle}`}
                    onClick={() => {
                      setActiveHandle(c.peer.handle);
                      setParams({ with: c.peer.handle });
                    }}
                    className={`w-full text-left flex items-center gap-3 p-3 md:p-4 border-b border-[#27272A] transition-colors ${
                      isActive ? "bg-[#121212]" : "hover:bg-[#121212]"
                    }`}
                  >
                    <div className="relative shrink-0">
                      <div className="h-12 w-12 bg-[#121212] border border-[#27272A]">
                        {c.peer.photo ? (
                          <img
                            src={c.peer.photo}
                            alt={c.peer.name}
                            className="h-12 w-12 object-cover"
                          />
                        ) : null}
                      </div>
                      {c.unread > 0 ? (
                        <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 bg-[#FF3E00] text-white font-mono text-[9px] flex items-center justify-center font-bold">
                          {c.unread}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-display font-bold text-sm truncate flex items-center gap-1">
                          {c.peer.name}
                          {c.peer.verified ? (
                            <SealCheck
                              size={12}
                              weight="fill"
                              className="text-[#FF3E00]"
                            />
                          ) : null}
                        </span>
                        <span className="font-mono text-[10px] text-[#A1A1AA] shrink-0">
                          {hhmm(c.last_at)}
                        </span>
                      </div>
                      <div
                        className={`text-xs truncate mt-0.5 ${
                          c.unread ? "text-white font-semibold" : "text-[#A1A1AA]"
                        }`}
                      >
                        {c.last_message || "· sin mensajes ·"}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </aside>

          <section className="flex flex-col min-h-0">
            {activePeer ? (
              <>
                <header className="flex items-center justify-between gap-3 p-4 border-b border-[#27272A]">
                  <Link
                    to={`/artista/${activePeer.handle}`}
                    className="flex items-center gap-3"
                  >
                    <div className="h-10 w-10 bg-[#121212] border border-[#27272A]">
                      {activePeer.photo ? (
                        <img
                          src={activePeer.photo}
                          alt={activePeer.name}
                          className="h-10 w-10 object-cover"
                        />
                      ) : null}
                    </div>
                    <div>
                      <div className="font-display font-bold flex items-center gap-1">
                        {activePeer.name}
                        {activePeer.verified ? (
                          <SealCheck size={13} weight="fill" className="text-[#FF3E00]" />
                        ) : null}
                      </div>
                      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                        @{activePeer.handle}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-1">
                    <IconAction icon={Phone} />
                    <IconAction icon={VideoCamera} />
                    <IconAction icon={DotsThree} />
                  </div>
                </header>

                <div
                  ref={scrollerRef}
                  className="flex-1 overflow-auto p-4 md:p-6 space-y-3 bg-[#050505]"
                >
                  {loadingConv ? (
                    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                      cargando…
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#525252]">
                      · empieza una conversación con @{activePeer.handle} ·
                    </div>
                  ) : (
                    messages.map((m) => {
                      const me = m.sender_id === user.id;
                      return (
                        <div
                          key={m.id}
                          data-testid={`dm-bubble-${m.id.slice(0, 12)}`}
                          className={`flex ${me ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[75%] px-4 py-2.5 ${
                              me
                                ? "bg-[#FF3E00] text-white"
                                : "bg-[#121212] border border-[#27272A] text-[#F4F4F5]"
                            }`}
                          >
                            <p className="text-[14px] leading-snug whitespace-pre-wrap">
                              {m.text}
                            </p>
                            <div
                              className={`font-mono text-[9px] mt-1 ${
                                me ? "text-white/70" : "text-[#A1A1AA]"
                              }`}
                            >
                              {hhmm(m.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {error ? (
                  <div className="font-mono text-[11px] text-[#FF8899] border-t border-[#FF0033] bg-[#FF0033]/10 px-4 py-2">
                    {error}
                  </div>
                ) : null}

                <form
                  onSubmit={onSend}
                  className="border-t border-[#27272A] p-3 flex items-center gap-2"
                >
                  <IconAction icon={Paperclip} />
                  <IconAction icon={Smiley} />
                  <input
                    data-testid="dm-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={`Mensaje para @${activePeer.handle}…`}
                    className="flex-1 bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-4 py-3 font-mono text-sm"
                  />
                  <button
                    data-testid="dm-send"
                    type="submit"
                    disabled={!draft.trim()}
                    className={`flex items-center gap-2 px-5 py-3 font-mono uppercase text-[10px] tracking-[0.2em] font-bold ${
                      draft.trim()
                        ? "bg-[#FF3E00] text-white"
                        : "bg-[#121212] text-[#525252]"
                    }`}
                  >
                    <PaperPlaneTilt size={14} weight="bold" /> Enviar
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 grid place-items-center text-center p-10">
                <div>
                  <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-3">
                    // SELECCIONA UN CHAT
                  </div>
                  <p className="font-display text-2xl text-[#E5E5E5] max-w-md leading-snug">
                    O abre un perfil y pulsa <span className="text-[#FF3E00]">Mensaje</span>.
                  </p>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}

function IconAction({ icon: Icon }) {
  return (
    <button
      type="button"
      className="h-10 w-10 inline-flex items-center justify-center border border-[#27272A] text-[#A1A1AA] hover:border-[#FF3E00] hover:text-[#FF3E00]"
    >
      <Icon size={16} />
    </button>
  );
}
